// El agente del Catálogo en el teléfono (ADR-2026-10-15-el-catalogo-en-el-telefono), como el de la
// app de Notion: con el catálogo a la vista el agente es una PASTILLA abajo; tocarla abre la CAJA
// de prompt sobre el catálogo (fondo transparente, el catálogo detrás); al mandar, la conversación
// sube como una HOJA desde abajo, y se baja (flecha o arrastre) para volver al catálogo. En el
// escritorio nada de esto existe: el agente vive en el panel del costado.
// Todo va con portal a body: un `fixed` montado adentro de una página se mete debajo del cascarón
// (`project_fixed_panel_stacking_trap`). Los z son los del panel del chat en el teléfono (110).
import { ChevronDown } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { ConversacionChatAtlas } from '../../../../chat-atlas/ui/ConversacionChatAtlas';
import { useAltoDelTeclado } from '../../../../chat-atlas/ui/useAltoDelTeclado';
import { CumbreMascot } from '../../../../components/CumbreMascot';
import type { ChatAttachment } from '../../agentAttachments';
import { CajaDeCatalogo } from './CajaDeCatalogo';
import { sumarAdjuntoAlBorrador, type BorradorDelAgente } from './borradorDelAgente';

export type EstadoDelAgenteMobile = 'pastilla' | 'caja' | 'chat';

/** Lo que dura la hoja en subir o bajar (la `duration-300` de su transición). */
const DURACION_MS = 300;
/** Cuánto hay que arrastrar la hoja hacia abajo para que se cierre. */
const ARRASTRE_PARA_CERRAR = 110;

// ── La pastilla ───────────────────────────────────────────────────────────────

export const PastillaDelAgente: React.FC<{
  texto: string;
  trabajando: boolean;
  /** Una respuesta o una pregunta del agente que la persona todavía no vio. */
  novedad: boolean;
  onAbrir: () => void;
  /** Sólo Cumbre, en un círculo: cuando la barra también lleva Descargar, Lanzamiento y
   *  Generar, el texto no entra (el texto queda como nombre accesible). */
  compacta?: boolean;
}> = ({ texto, trabajando, novedad, onAbrir, compacta = false }) => (
  <button
    type="button"
    onClick={onAbrir}
    aria-label={compacta ? texto : undefined}
    title={compacta ? texto : undefined}
    className={`relative flex h-12 items-center rounded-full bg-white shadow-[0_8px_24px_-10px_rgba(2,6,43,0.28),0_1px_3px_rgba(2,6,43,0.08)] ring-1 ring-black/[0.06] transition-transform active:scale-[0.98] ${
      compacta ? 'w-12 shrink-0 justify-center' : 'min-w-0 flex-1 gap-2.5 pl-1.5 pr-4 text-left'
    }`}
  >
    {/* Cumbre entera, sin círculo que la recorte. */}
    <span className="grid h-9 w-9 shrink-0 place-items-center">
      <CumbreMascot state={trabajando ? 'thinking' : 'idle'} size={30} decorativa className="cumbre--quieta" />
    </span>
    {!compacta && (
      <span className={`min-w-0 flex-1 truncate text-[15px] ${novedad || trabajando ? 'font-semibold text-brand-black' : 'text-[#3C3C43]/60'}`}>{texto}</span>
    )}
    {novedad && !trabajando && (
      <span
        className={`h-2.5 w-2.5 shrink-0 rounded-full bg-brand-primary ${compacta ? 'absolute right-0.5 top-0.5 ring-2 ring-white' : ''}`}
        aria-label="Sin leer"
      />
    )}
  </button>
);

// ── La caja, sobre el catálogo ────────────────────────────────────────────────

export const CajaDelAgenteMobile: React.FC<{
  borrador: BorradorDelAgente;
  onCambiarBorrador: (cambiar: (b: BorradorDelAgente) => BorradorDelAgente) => void;
  onCerrar: () => void;
  onEnviar: (texto: string, adjuntos: ChatAttachment[]) => void;
  /** La conversación entera, sin mandar nada (para releer lo que dijo el agente). */
  onVerConversacion: () => void;
  /** Por qué el último envío no salió (la caja se reabrió con lo que se había mandado). */
  aviso?: string | null;
}> = ({ borrador, onCambiarBorrador, onCerrar, onEnviar, onVerConversacion, aviso }) => {
  const { texto: valor, adjuntos, envio } = borrador;
  const teclado = useAltoDelTeclado(true);
  return createPortal(
    <div className="fixed inset-0 z-[110] md:hidden">
      {/* El catálogo sigue detrás: el fondo apenas se oscurece, y tocarlo cierra la caja. */}
      <button type="button" aria-label="Cerrar" onClick={onCerrar} className="animate-fade-in absolute inset-0 bg-[#02062B]/[0.06]" />
      <div
        className="animate-slide-up-sheet absolute inset-x-0 flex flex-col gap-2 px-3"
        style={{ bottom: teclado, paddingBottom: teclado ? 8 : 'max(env(safe-area-inset-bottom), 12px)' }}
      >
        <button
          type="button"
          onClick={onVerConversacion}
          className="self-center rounded-full bg-white/90 py-1.5 px-3.5 text-[13px] font-semibold text-brand-black shadow-sm ring-1 ring-black/[0.06] backdrop-blur-md"
        >
          Ver conversación
        </button>
        <div className="rounded-[22px] shadow-[0_12px_40px_-12px_rgba(2,6,43,0.35)]">
          <CajaDeCatalogo
            valor={valor}
            onCambiarValor={(texto) => onCambiarBorrador((b) => ({ ...b, texto }))}
            adjuntos={adjuntos}
            onAgregarAdjunto={(a) => onCambiarBorrador(sumarAdjuntoAlBorrador(envio, a))}
            onQuitarAdjunto={(id) => onCambiarBorrador((b) => ({ ...b, adjuntos: b.adjuntos.filter((a) => a.id !== id) }))}
            onEnviar={(texto) => {
              if (!texto.trim() && adjuntos.length === 0) return;
              onEnviar(texto, adjuntos);
            }}
            placeholder="Pedile un cambio al agente"
            alto="normal"
            autoFoco
          />
        </div>
        {aviso && (
          <p role="alert" className="self-center rounded-full bg-white/90 py-1.5 px-3.5 text-[13px] text-[#B42318] shadow-sm ring-1 ring-black/[0.06] backdrop-blur-md">
            {aviso}
          </p>
        )}
      </div>
    </div>,
    document.body,
  );
};

// ── La hoja con la conversación ───────────────────────────────────────────────

export const HojaDelAgente: React.FC<{ abierta: boolean; onCerrar: () => void }> = ({ abierta, onCerrar }) => {
  // Montada mientras está abierta o bajando; `arriba` es la posición (para que suba y baje animada).
  const [montada, setMontada] = useState(abierta);
  const [arriba, setArriba] = useState(false);
  const [arrastre, setArrastre] = useState(0);
  const desde = useRef<number | null>(null);
  const teclado = useAltoDelTeclado(montada);

  useEffect(() => {
    if (abierta) {
      setMontada(true);
      const raf = window.requestAnimationFrame(() => window.requestAnimationFrame(() => setArriba(true)));
      return () => window.cancelAnimationFrame(raf);
    }
    setArriba(false);
    const t = window.setTimeout(() => setMontada(false), DURACION_MS);
    return () => window.clearTimeout(t);
  }, [abierta]);

  if (!montada) return null;

  const soltar = () => {
    if (desde.current === null) return;
    desde.current = null;
    if (arrastre > ARRASTRE_PARA_CERRAR) onCerrar();
    setArrastre(0);
  };

  return createPortal(
    <div className="fixed inset-0 z-[110] md:hidden">
      <div
        className={`absolute inset-0 bg-[#02062B]/30 transition-opacity duration-300 ${arriba ? 'opacity-100' : 'opacity-0'}`}
        onClick={onCerrar}
        aria-hidden="true"
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-label="El agente del catálogo"
        className={`absolute inset-x-0 bottom-0 flex flex-col overflow-hidden rounded-t-[22px] bg-white shadow-2xl ${desde.current === null ? 'transition-transform duration-300 ease-out' : ''}`}
        style={{
          top: 'max(env(safe-area-inset-top), 12px)',
          transform: arriba ? `translateY(${arrastre}px)` : 'translateY(100%)',
          paddingBottom: teclado,
        }}
      >
        {/* La manija y la flecha: arrastrar hacia abajo o tocar la flecha vuelve al catálogo. */}
        <div
          className="shrink-0 touch-none select-none"
          onPointerDown={(e) => {
            desde.current = e.clientY;
            (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
          }}
          onPointerMove={(e) => {
            if (desde.current !== null) setArrastre(Math.max(0, e.clientY - desde.current));
          }}
          onPointerUp={soltar}
          onPointerCancel={soltar}
        >
          <div className="py-2">
            <div className="mx-auto h-[5px] w-9 rounded-full bg-black/[0.15]" />
          </div>
          <div className="flex justify-center px-3 pb-2">
            <button
              type="button"
              onClick={onCerrar}
              className="inline-flex items-center gap-1 rounded-full bg-white py-1.5 pl-2.5 pr-3.5 text-[13.5px] font-semibold text-brand-black shadow-sm ring-1 ring-black/[0.08] active:bg-black/[0.04]"
            >
              <ChevronDown className="h-4 w-4" strokeWidth={2.25} aria-hidden="true" />
              Ver catálogo
            </button>
          </div>
        </div>
        <div className="flex min-h-0 flex-1 flex-col">
          <ConversacionChatAtlas variante="panel" anchoColumna="max-w-full" padding="px-3" />
        </div>
      </div>
    </div>,
    document.body,
  );
};
